import React, { useEffect, useRef, useState } from 'react'
import {
    BulkActionDialog,
    BulkActionDialogActions,
    BulkActionField,
    bulkActionInputClassName,
} from '../../components/ui/BulkActionDialog'
import { Callout } from '../../components/ui/callout'
import { Combobox } from '../../components/ui/combobox'
import { customFieldApiError, customFieldsApi } from './api'
import {
    customFieldEntitySelectionId,
    EntitySelection,
    validateCustomFieldInput,
} from './ValueEditor'
import type { CustomFieldEntitySelection } from './ValueEditor'
import { CUSTOM_FIELD_DATA_TYPES, CUSTOM_FIELD_ENTITY_OPTIONS } from './types'
import type { CustomFieldEntityType } from './types'
import { useCustomFieldCatalog } from './useCustomFieldCatalog'

const fieldClass = bulkActionInputClassName

export interface CustomFieldAddValueModalProps {
    isOpen: boolean
    onClose: () => void
    profileId: string | null
    /** Set when the surrounding page locks entity type (e.g. a definition-scoped route). */
    lockedEntityType: CustomFieldEntityType | null
    /** Starting entity type when the picker is unlocked. */
    defaultEntityType: CustomFieldEntityType
    onAdded: (entity: { id: string; name: string }, entityType: CustomFieldEntityType) => void
}

export const CustomFieldAddValueModal = ({
    isOpen,
    onClose,
    profileId,
    lockedEntityType,
    defaultEntityType,
    onAdded,
}: CustomFieldAddValueModalProps) => {
    const [entityType, setEntityType] = useState<CustomFieldEntityType>(
        lockedEntityType ?? defaultEntityType,
    )
    const [entity, setEntity] = useState<CustomFieldEntitySelection | null>(null)
    const [drafts, setDrafts] = useState<Record<string, string | boolean | null>>({})
    const [error, setError] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
    const [saving, setSaving] = useState(false)
    const latestRequest = useRef(0)

    useEffect(() => {
        if (!isOpen) return
        setEntityType(lockedEntityType ?? defaultEntityType)
        setEntity(null)
        setDrafts({})
        setError(null)
        setFieldErrors({})
        setSaving(false)
        latestRequest.current++
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    const { catalog } = useCustomFieldCatalog(profileId, entityType)
    const definitions = catalog?.definitions ?? []
    const valueQuotaReached = (catalog?.value_count ?? 0) >= (catalog?.value_limit ?? 100000)

    const changeEntityType = (nextEntityType: CustomFieldEntityType) => {
        setEntityType(nextEntityType)
        setEntity(null)
        setDrafts({})
        setFieldErrors({})
    }

    const entityId = customFieldEntitySelectionId(entityType, entity)

    const submit = async () => {
        if (!profileId || !entityId || !entity) return
        const errors: Record<string, string> = {}
        const mutations: Array<{
            field_definition_id: string
            entity_id: string
            operation: 'set'
            value: string | boolean
            expected_version: null
        }> = []
        for (const definition of definitions) {
            const draft = drafts[definition.definition_id] ?? null
            if (draft === null) continue
            const validation = validateCustomFieldInput(definition, draft)
            if (validation) {
                errors[definition.definition_id] = validation
                continue
            }
            mutations.push({
                field_definition_id: definition.definition_id,
                entity_id: entityId,
                operation: 'set',
                value: draft,
                expected_version: null,
            })
        }
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            return
        }
        const request = ++latestRequest.current
        setSaving(true)
        setError(null)
        try {
            if (mutations.length > 0) {
                await customFieldsApi.mutateValues(profileId, entityType, mutations)
                if (request !== latestRequest.current) return
            }
            onAdded({ id: entityId, name: entity.name }, entityType)
            onClose()
        } catch (requestError) {
            if (request !== latestRequest.current) return
            setError(customFieldApiError(requestError))
        } finally {
            if (request === latestRequest.current) setSaving(false)
        }
    }

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={onClose}
            title="Add value"
            width="default"
            dismissible={!saving}
            footer={
                <BulkActionDialogActions
                    primaryLabel="Add"
                    loadingLabel="Adding…"
                    loading={saving}
                    disabled={!profileId || !entityId}
                    onPrimary={submit}
                    onCancel={onClose}
                />
            }
        >
            {!profileId && (
                <Callout intent="warning" title="Select an ad account" className="mb-[18px]" />
            )}
            {error && <Callout intent="danger" title={error} className="mb-[18px]" />}
            {valueQuotaReached && (
                <Callout
                    intent="warning"
                    title="Value quota reached — you can still add the row, but new values cannot be set"
                    className="mb-[18px]"
                />
            )}

            <BulkActionField label="Entity type" required>
                {lockedEntityType ? (
                    <div className={`${fieldClass} bg-gray-50 text-gray-600`}>
                        {CUSTOM_FIELD_ENTITY_OPTIONS.find((o) => o.value === lockedEntityType)
                            ?.label ?? lockedEntityType}
                    </div>
                ) : (
                    <Combobox
                        options={CUSTOM_FIELD_ENTITY_OPTIONS}
                        value={entityType}
                        onChange={(value) => changeEntityType(value as CustomFieldEntityType)}
                        className="w-full"
                    />
                )}
            </BulkActionField>

            <BulkActionField label="Entity" required>
                <EntitySelection
                    entityType={entityType}
                    value={entity}
                    onChange={setEntity}
                    disabled={!profileId}
                />
            </BulkActionField>

            {definitions.length === 0 && (
                <p className="mb-[18px] text-[13px] text-[#738091]">
                    No custom fields for this entity type yet.
                </p>
            )}

            {definitions.map((definition) => {
                const draft = drafts[definition.definition_id] ?? null
                const setDraft = (value: string | boolean | null) =>
                    setDrafts((current) => ({ ...current, [definition.definition_id]: value }))
                return (
                    <BulkActionField
                        key={definition.definition_id}
                        label={definition.name}
                        htmlFor={`custom-field-add-value-${definition.definition_id}`}
                        error={fieldErrors[definition.definition_id]}
                    >
                        {definition.data_type === CUSTOM_FIELD_DATA_TYPES.Boolean ? (
                            <select
                                id={`custom-field-add-value-${definition.definition_id}`}
                                className={fieldClass}
                                value={draft === null ? '' : draft ? 'true' : 'false'}
                                disabled={valueQuotaReached}
                                onChange={(event) =>
                                    setDraft(
                                        event.target.value === ''
                                            ? null
                                            : event.target.value === 'true',
                                    )
                                }
                            >
                                <option value="">Not set</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        ) : (
                            <input
                                id={`custom-field-add-value-${definition.definition_id}`}
                                className={fieldClass}
                                value={typeof draft === 'string' ? draft : ''}
                                disabled={valueQuotaReached}
                                onChange={(event) => setDraft(event.target.value || null)}
                                placeholder={
                                    definition.data_type === CUSTOM_FIELD_DATA_TYPES.Number
                                        ? '0.00'
                                        : 'Not set'
                                }
                                maxLength={
                                    definition.data_type === CUSTOM_FIELD_DATA_TYPES.String
                                        ? 250
                                        : undefined
                                }
                            />
                        )}
                    </BulkActionField>
                )
            })}
        </BulkActionDialog>
    )
}
